import { SectionCard } from '@atlas/ui-components'
import { Skeleton } from '@atlas/ui-primitives'
import type { ReactNode } from 'react'

export interface ActivityRow {
	id: string
	/** Short, already formatted — `09:12`, `Yst`, `Mon`. */
	time: string
	text: string
}

/**
 * The home page's "Recent activity" card — the newest audit-sink events as
 * quiet time + sentence rows, with an optional "Library pulse" footer note.
 */
export function RecentActivityCard({
	rows,
	isLoading,
	pulse,
	emptyMessage = 'No activity yet.',
}: {
	rows: ActivityRow[]
	isLoading?: boolean
	pulse?: ReactNode
	/** Shown instead of the default when there are no rows — e.g. a role-gated feed. */
	emptyMessage?: string
}) {
	return (
		<SectionCard title="Recent activity" className="p-0 px-6 py-5.5">
			{isLoading ? (
				<div aria-busy="true" className="border-t border-ds-border pt-3">
					<Skeleton shape="text" className="w-2/3" />
					<span className="sr-only">Loading…</span>
				</div>
			) : rows.length === 0 ? (
				<p className="border-t border-ds-border pt-3 text-sm text-ds-text-subtle">{emptyMessage}</p>
			) : (
				rows.map((row) => (
					<div key={row.id} className="flex gap-3 border-t border-ds-border py-2.75 text-ds-xs">
						<span className="w-9 flex-none pt-0.5 font-mono text-ds-3xs text-ds-text-subtle">{row.time}</span>
						<span className="min-w-0 leading-relaxed text-ds-text-muted">{row.text}</span>
					</div>
				))
			)}
			{pulse ? (
				<div className="mt-4 rounded-ds-lg border border-ds-border bg-ds-surface p-4">
					<span className="mb-1.5 block font-mono text-ds-3xs uppercase tracking-ds-wider text-ds-text-subtle">
						Library pulse
					</span>
					<div className="text-ds-xs leading-relaxed text-ds-text-muted">{pulse}</div>
				</div>
			) : null}
		</SectionCard>
	)
}
